// Uber Reports: upload Uber's day, week and month exports, see each one read at
// once, and see every Uber day checked across the exports. db/migrations/6604.
//
// Why. Uber's scheduled Payments file for 6 Oct 2026 stopped at 19:03. 170 night
// trips had no payment in any file and night drivers' pay fell back on their own
// declarations for nine days. A fresh export fixed it, but there was nowhere on
// the platform to put it, and nothing said the day was short.
//
// Every status, count and sentence on this page comes from Postgres
// (v_uber_report_check_day, uber_report_files). React only chooses a colour.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, FileUp, RefreshCw, UploadCloud } from 'lucide-react'
import { useGetUberReportChecks, useProcessUberReportUpload, useRereadUberReportDay, useSignUberReportUpload } from '../hooks/backend/uber'
import { Button } from '../lib/shadcn/button'
import { Spinner } from '../lib/shadcn/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../lib/shadcn/table'
import { Chip, type ChipTone } from '../components/interviews/Chip'
import { uploadSigned, type Signed } from '../utils/signedUpload'
import { formatDate, formatDateTime, formatZar } from '../utils/interviews'

type Num = number | string | null

export type UberCheckDay = {
  report_date: string
  trips_completed: Num
  trips_unpaid: Num
  payments_loaded: Num
  net_loaded: Num
  drive_daily_payments: Num
  day_payments: Num
  week_payments: Num
  month_payments: Num
  day_net: Num
  week_net: Num
  month_net: Num
  org_net: Num
  short_windows: Num
  status: string
  message: string
}

export type UberReportFile = {
  id: string
  file_name: string
  report_key: string | null
  period_start: string | null
  period_end: string | null
  period_kind: string | null
  row_count: Num
  first_event_at: string | null
  last_event_at: string | null
  totals: { net_zar?: number; cash_zar?: number; rows?: number } | null
  status: string
  outcome: string | null
  uploaded_at: string
  finished_at: string | null
  uploaded_by_name: string | null
  merged: boolean | null
}

type Checks = { days: UberCheckDay[]; files: UberReportFile[] }

type LocalUpload = { key: string; name: string; phase: 'uploading' | 'reading' | 'done' | 'failed'; message: string }

const STATUS: Record<string, { label: string; tone: ChipTone }> = {
  complete: { label: 'Complete', tone: 'green' },
  source_short_filled: { label: 'Filled', tone: 'green' },
  missing_payments: { label: 'Missing payments', tone: 'red' },
  control_mismatch: { label: 'Total mismatch', tone: 'red' },
  pay_not_refreshed: { label: 'Pay not refreshed', tone: 'amber' },
  no_data: { label: 'Not read yet', tone: 'neutral' },
}

const REPORT_LABEL: Record<string, string> = {
  payments_transaction: 'Payments (transactions)',
  trip_activity: 'Trip activity',
  payments_organisation: 'Payments (organisation total)',
  payments_driver: 'Payments (per driver)',
  driver_activity: 'Driver activity',
  driver_quality: 'Driver quality',
  driver_status: 'Driver status',
  vehicle_performance: 'Vehicle performance',
}

const FILE_TONE: Record<string, ChipTone> = {
  done: 'green', reading: 'amber', received: 'amber', failed: 'red', duplicate: 'neutral', unrecognised: 'red',
}

const n = (v: Num) => (v === null || v === undefined || v === '' ? null : Number(v))
const count = (v: Num) => (n(v) === null ? '—' : String(n(v)))

// A Postgres date reaches the browser as "2026-10-06T00:00:00.000Z" (postgres.js
// makes a Date), not "2026-10-06", so only the first ten characters are trusted.
const day = (s: string) => s.slice(0, 10)

function period(f: UberReportFile): string {
  if (!f.period_start) return '—'
  const start = day(f.period_start)
  if (!f.period_end) return formatDate(start)
  const last = new Date(Date.parse(day(f.period_end) + 'T00:00:00Z') - 86400000).toISOString().slice(0, 10)
  return last === start ? formatDate(start) : `${formatDate(start)} to ${formatDate(last)}`
}

export default function UberReports() {
  const checks = useGetUberReportChecks<Checks | null>()
  const sign = useSignUberReportUpload<Signed & { filename: string }>()
  const process = useProcessUberReportUpload<{ id: string; status: string; outcome?: string; report_key?: string; period_start?: string; period_end?: string; rows?: number }>()
  const reread = useRereadUberReportDay<{ ok: boolean }>()
  const [uploads, setUploads] = useState<LocalUpload[]>([])
  const [showAll, setShowAll] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [rereading, setRereading] = useState<Record<string, string>>({})
  const inputRef = useRef<HTMLInputElement>(null)

  const load = checks.trigger
  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { refresh() }, [refresh])

  const data = checks.data
  const files = data?.files ?? []
  const days = data?.days ?? []

  // Follow background reading until every file has an outcome.
  const busy = uploads.some((u) => u.phase === 'uploading' || u.phase === 'reading') || files.some((f) => f.status === 'reading' || f.status === 'received')
  useEffect(() => {
    if (!busy) return
    const t = setInterval(refresh, 4000)
    return () => clearInterval(t)
  }, [busy, refresh])

  // A local row is done once the server has an outcome for its file.
  useEffect(() => {
    setUploads((list) => list.map((u) => {
      if (u.phase !== 'reading') return u
      const f = files.find((x) => x.id === u.key)
      if (!f || f.status === 'reading' || f.status === 'received') return u
      return { ...u, phase: f.status === 'failed' || f.status === 'unrecognised' ? 'failed' : 'done', message: f.outcome ?? u.message }
    }))
  }, [files])

  const set = (key: string, patch: Partial<LocalUpload>) =>
    setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...patch } : u)))

  const send = useCallback(async (picked: FileList | File[]) => {
    for (const file of Array.from(picked)) {
      const tmp = `${file.name}-${Date.now()}-${Math.random()}`
      setUploads((list) => [{ key: tmp, name: file.name, phase: 'uploading', message: 'Uploading…' }, ...list])
      let stored = false
      try {
        if (!/\.csv$/i.test(file.name)) throw new Error('Upload the report as a .csv file, exactly as Uber gives it.')
        if (file.size > 50 * 1024 * 1024) throw new Error('That file is over 50 MB. Export a shorter period from Uber.')
        const signed = await sign.trigger({ filename: file.name })
        const path = await uploadSigned(signed, file)
        stored = true
        const res = await process.trigger({ path })
        if (!res) throw new Error('No answer came back for that file. Try again.')
        const what = res.report_key ? REPORT_LABEL[res.report_key] ?? res.report_key : null
        const msg = res.status === 'duplicate' || res.status === 'unrecognised'
          ? (res.outcome ?? 'Nothing changed.')
          : `${what ?? 'Report'}${res.period_start ? `, ${formatDate(res.period_start)}` : ''}${res.rows != null ? `, ${res.rows} rows` : ''}. Checking it against everything on record…`
        setUploads((list) => list.map((u) => (u.key === tmp
          ? { ...u, key: res.id, phase: res.status === 'reading' ? 'reading' : res.status === 'unrecognised' ? 'failed' : 'done', message: msg }
          : u)))
        refresh()
      } catch (e) {
        const why = e instanceof Error ? e.message : String(e)
        // Once the file is in storage it is not lost: uber_report_orphan_sweep_v1
        // reads anything the page could not (9951). On 2 Nov 2026 a red "failed"
        // here was the only trace of the trip activity file that held up a day's
        // statements, and nobody uploaded it again.
        set(tmp, stored
          ? { phase: 'done', message: `Uploaded. Reading did not start from this page (${why}), so the platform will read it on its own within 10 minutes. Nothing to do now; check the day below after that.` }
          : { phase: 'failed', message: why })
      }
    }
  }, [sign, process, refresh])

  const needsAttention = useMemo(
    () => days.filter((d) => ['missing_payments', 'control_mismatch', 'pay_not_refreshed'].includes(d.status)),
    [days],
  )
  const shownDays = showAll ? days : days.slice(0, 31)

  const readAgain = async (d: string) => {
    setRereading((r) => ({ ...r, [d]: 'Reading the day again…' }))
    try {
      await reread.trigger({ report_date: d })
      setRereading((r) => ({ ...r, [d]: 'Started. The figures update within a minute.' }))
      setTimeout(refresh, 30000)
    } catch (e) {
      setRereading((r) => ({ ...r, [d]: e instanceof Error ? e.message : String(e) }))
    }
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><FileUp className="h-6 w-6" /> Uber Reports</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Upload any export from Uber's Reports page: a day, a week or a month. Each file is read straight away and every
          day it covers is checked against the trips and against the other exports. When one export is short, the
          missing payments are taken from a fuller one; a figure is never replaced by a smaller one.
        </p>
      </header>

      {needsAttention.length > 0 && (
        <section className="space-y-2" aria-label="What is missing">
          <h2 className="flex items-center gap-2 text-lg font-semibold"><AlertTriangle className="h-5 w-5 text-red-600" /> What is missing</h2>
          <ul className="space-y-2">
            {needsAttention.map((d) => (
              <li key={d.report_date} className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/40 md:flex-row md:items-center md:justify-between">
                <div>
                  <span className="font-medium">{formatDate(d.report_date)}</span>{' '}
                  <Chip tone={STATUS[d.status]?.tone ?? 'neutral'}>{STATUS[d.status]?.label ?? d.status}</Chip>
                  <p className="mt-1">{d.message}</p>
                  {rereading[d.report_date] && <p className="mt-1 text-muted-foreground">{rereading[d.report_date]}</p>}
                </div>
                {d.status === 'pay_not_refreshed' && (
                  <Button size="sm" variant="outline" onClick={() => void readAgain(d.report_date)}>
                    <RefreshCw className="mr-1 h-4 w-4" /> Read the day again
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="Upload">
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files?.length) void send(e.dataTransfer.files) }}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${dragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/60'}`}
        >
          <UploadCloud className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">Drop Uber CSV files here, or click to choose</p>
          <p className="text-xs text-muted-foreground">Payments, trip activity, organisation totals and the other Uber exports. Several at once is fine.</p>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) void send(e.target.files); e.target.value = '' }}
          />
        </div>
        {uploads.length > 0 && (
          <ul className="mt-3 space-y-2">
            {uploads.map((u) => (
              <li key={u.key} className="flex items-start gap-2 rounded-md border p-3 text-sm">
                {u.phase === 'uploading' || u.phase === 'reading' ? <Spinner className="mt-0.5 h-4 w-4 shrink-0" />
                  : u.phase === 'done' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                  : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />}
                <div className="min-w-0">
                  <p className="truncate font-medium">{u.name}</p>
                  <p className="text-muted-foreground">{u.message}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2" aria-label="Every day">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Every day</h2>
          <div className="flex items-center gap-2">
            {checks.loading && <Spinner className="h-4 w-4" />}
            <Button size="sm" variant="ghost" onClick={refresh}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          An Uber day runs 04:00 to 04:00. Payments are counted per export; the figure on record is every payment from
          every export, each counted once.
        </p>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead className="text-right">Trips</TableHead>
                <TableHead className="text-right">Unpaid trips</TableHead>
                <TableHead className="text-right">Payments on record</TableHead>
                <TableHead className="text-right">Drive daily</TableHead>
                <TableHead className="text-right">Uploaded day</TableHead>
                <TableHead className="text-right">Week</TableHead>
                <TableHead className="text-right">Month</TableHead>
                <TableHead className="text-right">On record</TableHead>
                <TableHead className="text-right">Uber total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shownDays.map((d) => {
                const st = STATUS[d.status] ?? { label: d.status, tone: 'neutral' as ChipTone }
                // A day nothing has been read for shows its figures as missing, never as zero.
                const unread = d.status === 'no_data'
                const c = (v: Num) => (unread ? '—' : count(v))
                const short = (v: Num) => n(v) !== null && n(d.payments_loaded) !== null && (n(v) as number) < (n(d.payments_loaded) as number) - 2
                return (
                  <TableRow key={d.report_date}>
                    <TableCell className="whitespace-nowrap font-medium">{formatDate(d.report_date)}</TableCell>
                    <TableCell className="text-right">{c(d.trips_completed)}</TableCell>
                    <TableCell className={`text-right ${(n(d.trips_unpaid) ?? 0) > 0 ? 'font-semibold text-red-600' : ''}`}>{c(d.trips_unpaid)}</TableCell>
                    <TableCell className="text-right">{c(d.payments_loaded)}</TableCell>
                    <TableCell className={`text-right ${short(d.drive_daily_payments) ? 'text-amber-600' : ''}`}>{count(d.drive_daily_payments)}</TableCell>
                    <TableCell className={`text-right ${short(d.day_payments) ? 'text-amber-600' : ''}`}>{count(d.day_payments)}</TableCell>
                    <TableCell className={`text-right ${short(d.week_payments) ? 'text-amber-600' : ''}`}>{count(d.week_payments)}</TableCell>
                    <TableCell className={`text-right ${short(d.month_payments) ? 'text-amber-600' : ''}`}>{count(d.month_payments)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">{unread || n(d.net_loaded) === null ? '—' : formatZar(d.net_loaded)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">{n(d.org_net) === null ? '—' : formatZar(d.org_net)}</TableCell>
                    <TableCell className="min-w-[16rem]">
                      <Chip tone={st.tone}>{st.label}</Chip>
                      <p className="mt-1 text-xs text-muted-foreground">{d.message}</p>
                    </TableCell>
                  </TableRow>
                )
              })}
              {shownDays.length === 0 && !checks.loading && (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground">No days on record yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {days.length > 31 && (
          <Button size="sm" variant="ghost" onClick={() => setShowAll((s) => !s)}>{showAll ? 'Show the last month' : `Show all ${days.length} days`}</Button>
        )}
      </section>

      <section className="space-y-2" aria-label="Files">
        <h2 className="text-lg font-semibold">Files uploaded</h2>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Report</TableHead>
                <TableHead>Covers</TableHead>
                <TableHead className="text-right">Rows</TableHead>
                <TableHead>First to last</TableHead>
                <TableHead className="text-right">Your earnings</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="max-w-[16rem] truncate" title={f.file_name}>{f.file_name}</TableCell>
                  <TableCell className="whitespace-nowrap">{f.report_key ? REPORT_LABEL[f.report_key] ?? f.report_key : '—'}</TableCell>
                  <TableCell className="whitespace-nowrap">{period(f)}{f.period_kind && f.period_kind !== 'day' ? ` (${f.period_kind})` : ''}</TableCell>
                  <TableCell className="text-right">{count(f.row_count)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {f.first_event_at ? `${formatDateTime(f.first_event_at)} to ${formatDateTime(f.last_event_at)}` : '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">{f.totals?.net_zar != null ? formatZar(f.totals.net_zar) : '—'}</TableCell>
                  <TableCell className="min-w-[18rem]">
                    <Chip tone={FILE_TONE[f.status] ?? 'neutral'}>{f.status === 'done' ? 'Read' : f.status === 'reading' ? 'Reading' : f.status}</Chip>
                    <p className="mt-1 text-xs text-muted-foreground">{f.outcome}</p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{f.uploaded_by_name ?? '—'}<br />{formatDateTime(f.uploaded_at)}</TableCell>
                </TableRow>
              ))}
              {files.length === 0 && !checks.loading && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No files uploaded yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  )
}
